import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';
import restFactory from 'v2/utils/restFactory';
import { getRootDomain } from 'v2/utils/formValidator';
import {
  DataProviderEntity,
  DataVendor,
  DjangoListResponse,
  RetrievedCompanyInfo,
} from 'v2/models';

type FormDataVendor = Pick<
  DataVendor,
  'name' | 'classification' | 'description' | 'avatar_url'
> &
  Partial<Pick<DataVendor, 'data_provider'>>;

export function getFormDataVendor(
  externalCompany: RetrievedCompanyInfo
): FormDataVendor {
  return {
    name: externalCompany.display_name,
    classification: externalCompany.industry,
    description: externalCompany.description || '',
    avatar_url: externalCompany.avatar_url || '',
  };
}

interface SearchResult {
  isNew: boolean;
  dataVendor: FormDataVendor;
}

export default function useGetDataVendorByDomain(url: string) {
  const rootDomain = getRootDomain(url);
  const query = useQuery<SearchResult | undefined>(
    ['useGetDataVendorByDomain', rootDomain],
    async () => {
      const dataVendor = await restFactory.get<DjangoListResponse<DataVendor>>(
        `/dj/api/v1/data_vendor/`,
        { domain__iexact: rootDomain || '' }
      );
      if (dataVendor?.count) {
        return { dataVendor: dataVendor?.results[0], isNew: false };
      }

      const dataProvider = await restFactory.get<
        DjangoListResponse<DataProviderEntity>
      >(`/dj/api/v1/data_provider/`, {
        domain__iexact: rootDomain,
        site_name__iexact: 'web',
      });
      if (dataProvider?.count) {
        const dataProviderData = dataProvider?.results[0];
        return {
          isNew: true,
          dataVendor: {
            data_provider: dataProviderData.id,
            name: dataProviderData.name,
            classification: dataProviderData.classification || '',
            description: dataProviderData?.long_description || '',
            avatar_url: dataProviderData?.avatar_url || '',
          },
        };
      }

      const externalCompany = await restFactory.get<RetrievedCompanyInfo>(
        `/dj/api/v1/data_vendor/retrieve_company_info`,
        { domain: rootDomain || '' }
      );
      return {
        isNew: true,
        dataVendor: getFormDataVendor(externalCompany),
      };
    },
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: false,
      keepPreviousData: false,
    }
  );

  const debouncedSearch = React.useRef(
    debounce(async (_value: string) => {
      query.refetch();
    }, 500)
  ).current;

  useEffect(() => {
    if (rootDomain) {
      debouncedSearch(rootDomain);
    } else {
      debouncedSearch.cancel();
      query.remove();
    }
  }, [rootDomain]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return query;
}
